import { useCallback, useRef, useState } from "react";

/**
 * useSwipeToDelete — pointer-based swipe-left-to-delete for mobile rows.
 * Activates only for touch pointers (mouse users use the × button).
 *
 * Usage:
 *   const { swipeProps, swipeStyle, revealPct } = useSwipeToDelete(onDelete);
 *   <div {...swipeProps} style={swipeStyle}> ... </div>
 *
 * `revealPct` (0..1) can be used to reveal a red delete backdrop underneath.
 */
export const useSwipeToDelete = (onDelete, { threshold = 80 } = {}) => {
  const [dx, setDx] = useState(0);
  const [isSwiping, setSwiping] = useState(false);
  const state = useRef({ startX: 0, startY: 0, active: false, isTouch: false });

  const reset = useCallback(() => {
    setDx(0);
    setSwiping(false);
    state.current.active = false;
  }, []);

  const swipeProps = {
    onPointerDown: (e) => {
      // Only touch/pen; ignore mouse users
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;
      state.current.startX = e.clientX;
      state.current.startY = e.clientY;
      state.current.active = true;
      state.current.isTouch = true;
    },
    onPointerMove: (e) => {
      if (!state.current.active) return;
      const dxNow = e.clientX - state.current.startX;
      const dyNow = e.clientY - state.current.startY;
      // Only lock into swipe if primarily horizontal AND leftward, else bail
      if (!isSwiping) {
        if (Math.abs(dyNow) > Math.abs(dxNow)) {
          // Vertical scroll — let the browser handle it
          state.current.active = false;
          return;
        }
        if (dxNow > 8) {
          // Right swipe — ignore
          state.current.active = false;
          return;
        }
        if (dxNow < -8) setSwiping(true);
      }
      // Clamp: only allow leftward pull
      const next = Math.min(0, dxNow);
      setDx(next);
    },
    onPointerUp: () => {
      if (!state.current.active) { reset(); return; }
      if (dx <= -threshold) {
        // Commit delete; snap to full width first then invoke
        setDx(-9999);
        setTimeout(() => { try { onDelete?.(); } finally { reset(); } }, 120);
      } else {
        reset();
      }
    },
    onPointerCancel: reset,
  };

  const swipeStyle = {
    transform: dx === 0 ? undefined : `translateX(${dx}px)`,
    transition: isSwiping ? "none" : "transform 200ms ease-out",
    touchAction: "pan-y",
    willChange: dx === 0 ? undefined : "transform",
  };

  const revealPct = Math.min(1, Math.abs(dx) / threshold);
  return { swipeProps, swipeStyle, revealPct, isSwiping };
};
